import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import '../../styles/overview.scss'
import { InfoCard2 } from '../../../widgets/components/UI/InfoCard2'
import TextInput from '../../../widgets/components/Input/TextInput'
import { Col, Modal, Row } from 'react-bootstrap'
import { Form, Formik } from 'formik'
import { ToolTipUIForModal } from '../../../widgets/components/UI/ToolTipUIForModal'
import { SelectInput } from '../../../widgets/components/Input/SelectInput'
import TagInput from '../../../widgets/components/Input/TagInput'
import { useAuth } from '../../../auth'
import { createTemplate, getAllUsers, getClassification } from '../../core/_requests'
import { toast } from 'react-toastify'
import Skeleton from 'react-loading-skeleton'
import * as Yup from 'yup'

const TemplateReportModal = ({ isActive, setIsActive, getAllTemplates, setTabKey }: any) => {
  const { formatMessage } = useIntl()
  const { companyId } = useAuth()
  const [departmentList, setDepartmentList] = useState<any>([])
  const [categoryList, setCategoryList] = useState<any>([])
  const [categoryListLoading, setCategoryListLoading] = useState<boolean>(false)
  const [isLoading, setLoading] = useState<boolean>(false)
  const [emailList, setEmailList] = useState<any[]>([])
  const [emailListLoading, setEmailListLoading] = useState<boolean>(false)

  const initialValues = {
    templateName: '',
    activityType: '',
    recordAccess: false,
    permissionChanges: false,
    unauthorized: false,
    linkSharing: false,
    geoLocation: false,
    validUsers: '',
    departmentLists: '',
    categoryLists: '',
    format: 'PDF',
  }

  const getDepartmentList = async () => {
    if (!companyId) return
    const pageSize = 10
    const page = 1
    const type = 'LevelOne'
    const All = true
    try {
      const {
        data: { data, success, errors },
      } = await getClassification(companyId, type, All, pageSize, page)
      if (success) {
        setDepartmentList(
          data?.data.map((department: any) => ({
            id: department.levelOneId,
            value: department.name,
            label: department.name,
          }))
        )
      } else {
        toast.error(errors.name)
      }
    } catch (err) { }
  }

  useEffect(() => {
    if (isActive) getDepartmentList()
  }, [isActive]) // eslint-disable-line react-hooks/exhaustive-deps

  const getCategoryList = async (categoryIds: string[]) => {
    setCategoryListLoading(true)
    setCategoryList([])
    if (!companyId || departmentList.length === 0 || categoryIds.length === 0) return

    const pageSize = 10
    const page = 1
    const type = 'LevelTwo'
    const All = true

    try {
      const apiCalls = categoryIds.map(async (categoryId) => {
        const {
          data: { data, success, errors },
        } = await getClassification(companyId, type, All, pageSize, page, categoryId)
        if (success && data?.data?.length > 0) {
          return data.data.map((category: any) => ({
            id: category.levelTwoId,
            value: category.name,
            label: category.name,
          }))
        } else if (errors?.length > 0) {
          toast.error(errors[0])
        }
        return []
      })

      const allCategoryLists = await Promise.all(apiCalls)
      const combinedCategories = allCategoryLists.flat()

      setCategoryList([...combinedCategories])
    } catch (err) {
      console.error('Error fetching categories:', err)
    } finally {
      setCategoryListLoading(false)
    }
  }

  const userSchema = Yup.object().shape({
    templateName: Yup.string().nullable().required('Template name is required'),
    activityType: Yup.string().nullable().required('Activity Type is required'),
    validUsers: Yup.array()
      .min(1, 'At least one user should be selected.')
      .required('At least one user should be selected.'),
    departmentLists: Yup.array()
      .min(1, 'At least one department must be selected.')
      .required('Departments are required.'),
    categoryLists: Yup.array()
      .min(1, 'At least one category must be selected.')
      .required('Departments are required.'),
  })

  const handleSubmit = async (values: any, { resetForm }: any) => {
    try {
      if (!companyId) return
      setLoading(true)
      const payload = {
        name: values?.templateName,
        reportType: values?.activityType,
        recordAccess: values?.recordAccess,
        permissionChanges: values?.permissionChanges,
        unauthorized: values?.unauthorized,
        linkSharing: values?.linkSharing,
        geoLocation: values?.geoLocation,
        format: values?.format,
        listOfUser: values?.validUsers?.map((user: any) => user.id).join(','),
        departmentList: values?.departmentLists.map((department: any) => department.id),
        categoryList: values?.categoryLists.map((category: any) => category.id),
      }

      const {
        data: { success, errors },
      } = await createTemplate(companyId, payload)
      if (success) {
        toast.success('Template created successfully')
        resetForm()
        setIsActive(false)
        getAllTemplates()
        setTabKey(3)
      } else {
        toast.error(errors.name)
      }
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
    }
  }

  const getAllEmailList = async () => {
    setEmailListLoading(true)
    if (!companyId) return
    try {
      const {
        data: { data, success },
      } = await getAllUsers(companyId)
      if (success) {
        const formattedData = data.map((item: any) => ({
          id: item.userId,
          value: item.name,
          label: item.name,
        }))

        setEmailList(formattedData)
      }
    } catch (err) {
      console.log(err)
    } finally {
      setEmailListLoading(false)
    }
  }

  useEffect(() => {
    if (isActive) getAllEmailList()
  }, [isActive]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div className='mt-4'>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={userSchema}
          enableReinitialize={true}
          validateOnChange
          validateOnMount
        >
          {(formik) => {
            return (
              <>
                <Form onSubmit={formik.handleSubmit}>
                  <Modal
                    show={isActive}
                    onHide={() => {
                      formik.resetForm()
                      setIsActive(false)
                    }}
                    size='lg'
                    fullscreen
                  >
                    <Modal.Header closeButton>
                      <h2>{formatMessage({ id: 'Create Template for Report' })}</h2>
                    </Modal.Header>
                    <Modal.Body>
                      <div className='container'>
                        <div className='mt-4'>
                          <InfoCard2
                            title={'Creating Custom Report Templates'}
                            desc={
                              'This page allows you to create and save custom report templates for specific activities, users, departments, and categories. Choose the actions, users, and file categories you want to track, and generate reports in your preferred format. Use this feature to streamline report generation for auditing or compliance.'
                            }
                            slug={
                              'https://help.recordskeeper.ai/en/articles/10411623-how-to-create-custom-report-templates'
                            }
                          />
                        </div>

                        <div className='rounded-2 mt-6'>
                          <h2 className='font-size-16'>
                            {formatMessage({ id: 'Create New Template' })}
                          </h2>

                          <>
                            <Row>
                              <Col md={6}>
                                <TextInput
                                  fieldType='text'
                                  label={formatMessage({ id: 'Template Name' })}
                                  fieldName='templateName'
                                  formik={formik}
                                  placeholder={formatMessage({ id: 'Enter Template Name' })}
                                  toolTipText={formatMessage({
                                    id: 'Enter a unique name for this schedule to easily identify it later.',
                                  })}
                                  isStarRequired={true}
                                  isTooltipForModal={true}
                                />
                              </Col>
                            </Row>

                            <div className='mt-4'>
                              <h2 className='font-size-16 m-0'>
                                {formatMessage({ id: 'Report Information & Details' })}
                              </h2>

                              <Row className='mt-4'>
                                <Col md={8}>
                                  <SelectInput
                                    label={formatMessage({
                                      id: 'I want to generate the report of below activity type (e.g., file access, upload, modification)',
                                    })}
                                    fieldName='activityType'
                                    placeholder={formatMessage({ id: 'Access Activities' })}
                                    formik={formik}
                                    toolTipText={formatMessage({
                                      id: 'Select the type of activities to include in the report, such as file access, upload, or modification.',
                                    })}
                                    options={[
                                      'ACCESS REPORT',
                                      'EDIT REPORT',
                                      'MODIFICATION REPORT',
                                      'DELETION REPORT',
                                      'UPLOAD REPORT',
                                    ]}
                                    isTooltipForModal={true}
                                  />
                                </Col>
                              </Row>

                              <Row>
                                <Col md={12}>
                                  <label className='form-label font-size-13 text-dark text-capitalize ms-3 ms-md-0'>
                                    {formatMessage({ id: 'For below actions' })}*{' '}
                                    <ToolTipUIForModal
                                      tooltipText={formatMessage({
                                        id: 'Choose the specific actions you want to report on, such as record access or permission changes.',
                                      })}
                                    />
                                  </label>

                                  <div className='d-flex w-100  align-items-center'>
                                    <div className='d-flex align-items-center gap-2'>
                                      <TextInput
                                        fieldName={'recordAccess'}
                                        formik={formik}
                                        customText={formatMessage({ id: 'Record Access' })}
                                        placeholder=''
                                        fieldType={'checkbox'}
                                        isCheckbox={true}
                                        isWidthNotRequired={true}
                                        isTooltipNotRequired={true}
                                      />
                                    </div>

                                    <div className='d-flex align-items-center gap-2'>
                                      <TextInput
                                        fieldName={'permissionChanges'}
                                        formik={formik}
                                        customText={formatMessage({ id: 'Permission Changes' })}
                                        placeholder=''
                                        fieldType={'checkbox'}
                                        isCheckbox={true}
                                        isWidthNotRequired={true}
                                        isTooltipNotRequired={true}
                                      />
                                    </div>

                                    <div className='d-flex align-items-center gap-2'>
                                      <TextInput
                                        fieldName={'unauthorized'}
                                        formik={formik}
                                        customText={formatMessage({
                                          id: 'Unauthorized Access Attempts',
                                        })}
                                        placeholder=''
                                        fieldType={'checkbox'}
                                        isCheckbox={true}
                                        isWidthNotRequired={true}
                                        isTooltipNotRequired={true}
                                      />
                                    </div>

                                    <div className='d-flex align-items-center gap-2'>
                                      <TextInput
                                        fieldName={'linkSharing'}
                                        formik={formik}
                                        customText={formatMessage({ id: 'Link Sharing' })}
                                        placeholder=''
                                        fieldType={'checkbox'}
                                        isCheckbox={true}
                                        isWidthNotRequired={true}
                                        isTooltipNotRequired={true}
                                      />
                                    </div>

                                    <div className='d-flex align-items-center gap-2'>
                                      <TextInput
                                        fieldName={'geoLocation'}
                                        formik={formik}
                                        customText={formatMessage({ id: 'Geolocation of Access' })}
                                        placeholder=''
                                        fieldType={'checkbox'}
                                        isCheckbox={true}
                                        isWidthNotRequired={true}
                                        isTooltipNotRequired={true}
                                      />
                                    </div>
                                  </div>
                                </Col>
                              </Row>

                              <Row className='mt-0'>
                                <Col md={8}>
                                  {emailListLoading ? (
                                    <>
                                      <div className='w-100'>
                                        <Skeleton
                                          height={20}
                                          width={250}
                                          baseColor='var(--kt-skeleton-bg-color)'
                                          highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                        />
                                      </div>
                                      <div className='mt-2'>
                                        <Skeleton
                                          height={30}
                                          baseColor='var(--kt-skeleton-bg-color)'
                                          highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                        />
                                      </div>
                                    </>
                                  ) : (
                                    <TagInput
                                      label={formatMessage({ id: 'Of below Users' })}
                                      initialOptions={emailList}
                                      placeholder={formatMessage({ id: 'Add User' })}
                                      tooltip={formatMessage({
                                        id: 'Select users whose activities should be included in this report.',
                                      })}
                                      fieldName='validUsers'
                                      formik={formik}
                                    />
                                  )}
                                </Col>
                              </Row>

                              <Row className='mt-4'>
                                <Col md={6}>
                                  {departmentList?.length > 0 ? (
                                    <TagInput
                                      label={formatMessage({ id: 'From these departments' })}
                                      initialOptions={departmentList}
                                      placeholder={formatMessage({ id: 'Add Department' })}
                                      tooltip={formatMessage({
                                        id: 'Select departments to filter activities and records belonging to those specific departments.',
                                      })}
                                      fieldName='departmentLists'
                                      formik={formik}
                                      getCategoryId={(ids: string[]) => {
                                        getCategoryList(ids)
                                      }}
                                    />
                                  ) : (
                                    <>
                                      <div className='w-100'>
                                        <Skeleton
                                          height={20}
                                          width={250}
                                          baseColor='var(--kt-skeleton-bg-color)'
                                          highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                        />
                                      </div>
                                      <div className='mt-2'>
                                        <Skeleton
                                          height={30}
                                          baseColor='var(--kt-skeleton-bg-color)'
                                          highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                        />
                                      </div>
                                    </>
                                  )}
                                </Col>

                                <Col md={6}>
                                  {!categoryListLoading && departmentList?.length > 0 ? (
                                    <TagInput
                                      label={formatMessage({ id: 'From these categories' })}
                                      initialOptions={categoryList}
                                      placeholder={formatMessage({ id: 'Add Category' })}
                                      tooltip={formatMessage({
                                        id: 'Filter records by categories to include specific document groups in the report.',
                                      })}
                                      fieldName='categoryLists'
                                      formik={formik}
                                    />
                                  ) : (
                                    <>
                                      <div className='w-100'>
                                        <Skeleton
                                          height={20}
                                          width={250}
                                          baseColor='var(--kt-skeleton-bg-color)'
                                          highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                        />
                                      </div>
                                      <div className='mt-2'>
                                        <Skeleton
                                          height={30}
                                          baseColor='var(--kt-skeleton-bg-color)'
                                          highlightColor='var(--kt-skeleton-highlight-bg-color)'
                                        />
                                      </div>
                                    </>
                                  )}
                                </Col>
                              </Row>

                              <div className='d-md-flex flex-md-column mt-4'>
                                <div className='d-flex form-label  text-dark font-size-13'>
                                  {formatMessage({ id: 'In this output format' })}{' '}
                                  <ToolTipUIForModal
                                    tooltipText={formatMessage({
                                      id: 'Choose the file format for exporting the report, either PDF or XLS.',
                                    })}
                                  />
                                </div>
                                <div className='d-flex align-items-center gap-4'>
                                  {/* <div className='d-flex align-items-center gap-1'>
                                    <input
                                      type='radio'
                                      name='format'
                                      defaultChecked={formik?.values?.format === 'PDF'}
                                      value='PDF'
                                      onChange={formik.handleChange}
                                      width={48}
                                      height={48}
                                    />
                                    <label htmlFor='employee-yes'>
                                      {formatMessage({id: 'PDF'})}
                                    </label>
                                  </div> */}
                                  <div className='d-flex align-items-center gap-1'>
                                    <input
                                      type='radio'
                                      name='format'
                                      defaultChecked={formik?.values?.format === 'XLS'}
                                      onChange={formik.handleChange}
                                      value='XLS'
                                      width={48}
                                      height={48}
                                    />
                                    <label htmlFor='xls'>{formatMessage({ id: 'XLS' })}</label>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </>
                        </div>
                      </div>
                    </Modal.Body>
                    <Modal.Footer className='container px-0'>
                      <div className='d-md-flex w-100 justify-content-end px-md-0 px-0 gap-3'>
                        <button
                          type='button'
                          className='btn bg-light custom-button-property text-muted w-auto'
                          onClick={() => {
                            formik.resetForm()
                            setIsActive(false)
                          }}
                        >
                          {formatMessage({ id: 'Cancel' })}
                        </button>

                        <button
                          type='submit'
                          className='btn bg-primary text-white custom-button-property w-auto'
                          disabled={!formik.isValid || isLoading}
                          onClick={formik.submitForm}
                        >
                          {!isLoading ? (
                            <span className='indicator-label font-size-13 p-0'>
                              {formatMessage({ id: 'Create Template' })}
                            </span>
                          ) : (
                            <span className='indicator-label font-size-13 p-0'>
                              {formatMessage({ id: 'Please wait...' })}
                              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                            </span>
                          )}
                        </button>
                      </div>
                    </Modal.Footer>
                  </Modal>
                </Form>
              </>
            )
          }}
        </Formik>
      </div>
    </>
  )
}

export default TemplateReportModal
